import type { UserDataLoginRecord } from '@navio/nsdk';
import { URLKeys, noddiAsync } from 'noddi-async';
import { ApiErrorMessage, ErrorCodes, ErrorMessage, useAppForm } from 'noddi-ui-web';
import { commonTexts, errorCodesEnglish, isPhoneNumberValid } from 'noddi-util';
import { useEffect, useRef, useState } from 'react';
import type { UserSessionData } from '../../contexts/AuthContext';
import { useAuthContext } from '../../contexts/useAuthContext';
import { toFailureProps, tracking } from '../../tracking';
import { userRegistrationFormSchema, type UserRegistrationFormValues } from './userRegistrationFormValidators';

type FormValues = UserRegistrationFormValues;

type Props = {
  phoneNumber: string;
  skipPasswordCreation?: boolean;
  trackUserIdentity?: (userData: UserDataLoginRecord) => void;
  customCTAText?: string;
  orgProperties?: {
    orgName: string;
    orgNumber: string;
    departmentName: string;
    brandId: number;
    onCreated: (userGroupId: number) => void;
  };
  onLoginSuccess?: (userData: UserSessionData) => void;
  translations?: {
    thisFieldIsRequired: string;
    thisFieldMustBeAtLeast2CharactersLong: string;
    mustProvideAValidEmailAddress: string;
    passwordsMustMatch: string;
    thePasswordMustBeAtLeast8CharactersLong: string;
    firstName: string;
    surname: string;
    email: string;
    password: string;
    confirmPassword: string;
    userInformation: string;
    createUser: string;
  };
  errorCodes?: ErrorCodes;
};

export const TanstackUserRegistrationForm = ({
  phoneNumber,
  skipPasswordCreation,
  trackUserIdentity,
  customCTAText,
  orgProperties,
  onLoginSuccess,
  translations,
  errorCodes
}: Props) => {
  const texts = translations ?? commonTexts.registrationTranslations;
  const errorCodeTexts = errorCodes ?? errorCodesEnglish;
  const { loginUser } = useAuthContext();
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const defaultValues: FormValues = {
    firstName: '',
    surname: '',
    email: '',
    password: '',
    confirmPassword: ''
  };

  const { mutateAsync: createUser, error: createUserError } = noddiAsync.usePost({
    type: URLKeys.postAccount,
    queryConfig: {
      onSuccess: (output) => {
        trackUserIdentity?.(output.data.user);
        loginUser(output.data);
        onLoginSuccess?.(output.data);
      },
      onError: (error) => {
        tracking.track('userRegistrationFailed', toFailureProps(error, 'create_user'));
      }
    }
  });

  const { mutateAsync: createOrgUser, error: orgUserError } = noddiAsync.usePost({
    type: URLKeys.postUserWithOrganization,
    queryConfig: {
      onSuccess: ({ data }) => {
        trackUserIdentity?.(data.user);
        loginUser(data);
        onLoginSuccess?.(data);
      },
      onError: (error) => {
        tracking.track('orgUserRegistrationFailed', toFailureProps(error, 'create_org_user'));
      }
    }
  });

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }, [ref]);

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: userRegistrationFormSchema(!!skipPasswordCreation, texts)
    },
    onSubmit: async ({ value }) => {
      // The verified SMS number is not a form field. Refuse signup when it is missing.
      // Email and name are required by the schema and by signup create.
      if (!isPhoneNumberValid(phoneNumber)) {
        setPhoneError(texts.thisFieldIsRequired);
        return;
      }
      setPhoneError(null);

      if (orgProperties) {
        const { data } = await createOrgUser({
          firstName: value.firstName,
          lastName: value.surname,
          phoneNumber,
          email: value.email,
          organizationName: orgProperties.orgName,
          organizationNumber: orgProperties.orgNumber,
          departmentName: orgProperties.departmentName,
          brandId: orgProperties.brandId
        });
        orgProperties.onCreated(data.userGroupId);
        return;
      }
      await createUser({
        firstName: value.firstName,
        lastName: value.surname,
        phoneNumber,
        email: value.email,
        password: value.password || undefined,
        confirmPassword: value.confirmPassword || undefined
      });
    }
  });

  const ctaText = customCTAText ?? texts.createUser;
  const registrationError = createUserError ?? orgUserError;

  return (
    <form.AppForm>
      <form
        className='flex flex-col gap-2'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <h6 className='font-semibold'>{texts.userInformation}</h6>
        <form.AppField name='firstName'>{(field) => <field.Input ref={ref} label={texts.firstName} />}</form.AppField>
        <form.AppField name='surname'>{(field) => <field.Input label={texts.surname} />}</form.AppField>
        <form.AppField name='email'>{(field) => <field.Input label={texts.email} />}</form.AppField>
        {!skipPasswordCreation && (
          <div className='flex flex-col gap-2'>
            <form.AppField name='password'>
              {(field) => <field.Input autoComplete='new-password' label={texts.password} type='password' />}
            </form.AppField>
            <form.AppField name='confirmPassword'>
              {(field) => <field.Input autoComplete='new-password' label={texts.confirmPassword} type='password' />}
            </form.AppField>
          </div>
        )}
        {phoneError && <ErrorMessage message={phoneError} />}
        {registrationError && <ApiErrorMessage errorCodes={errorCodeTexts} error={registrationError} />}
        <form.SubscribeButton className='mt-2 w-full' label={ctaText} />
      </form>
    </form.AppForm>
  );
};
